import {
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ConfigContext } from '../../../context';
import {
  Story,
  StoryCardCategory,
  StoryCardCategoryTemplate,
  StoryCardTemplate,
} from '../../../services';
import { getLocale, getPostFieldsFromCard } from '../../utils';
import { Button, ButtonType } from '../Button';
import { CategoryTemplateSelectionModal } from './CategoryTemplateSelectionModal';
import { CreateCardModal } from './CreateCardModal';

interface CreateCardProps {
  postInStories: Story[];
  categories: StoryCardCategory[];
  templates: StoryCardTemplate[];
  onMobilePanelClose?: () => void;
}

export const CreateCard: FunctionComponent<CreateCardProps> = memo(
  ({ postInStories, categories, templates, onMobilePanelClose }) => {
    const { t } = useTranslation();

    const [searchParams] = useSearchParams();

    const { showForReader } = useContext(ConfigContext).config.elements.addContent;

    const [postInStoriesFiltered, setPostInStoriesFiltered] = useState<Story[]>(postInStories);

    const [isTemplatePost, setIsTemplatePost] = useState<boolean>(false);

    const defaultCreateCardModalOpen = useMemo(
      () => searchParams.get('createCard') === 'true',
      [searchParams]
    );

    const defaultCategoryId = useMemo(
      () => parseInt(searchParams.get('categoryId') ?? '', 10),
      [searchParams]
    );

    const [isCreateCardModalOpen, setIsCreateCardModalOpen] = useState<boolean>(
      defaultCreateCardModalOpen && !defaultCategoryId
    );

    const [categoryId, setCategoryId] = useState<number | undefined>(
      defaultCreateCardModalOpen ? defaultCategoryId : undefined
    );

    const [filledFields, setFilledFields] = useState<ReturnType<typeof getPostFieldsFromCard>>();

    const storyCardTemplateSelectHandler = useCallback(
      ({ story, storyCard }: Pick<StoryCardTemplate, 'story' | 'storyCard'>) => {
        setIsTemplatePost(true);
        setFilledFields(getPostFieldsFromCard(storyCard));
        setPostInStoriesFiltered(postInStories.filter(({ id }) => id === story?.id));
        setIsCreateCardModalOpen(true);
      },
      [postInStories]
    );

    const addContentOptions = useMemo(
      () =>
        [
          ...categories
            .filter(({ settings: { showAsOption } }) => showAsOption)
            .map(({ id, settings: { buttonTextEn, buttonTextDe } }) => ({
              title: getLocale() === 'en-US' ? buttonTextEn : buttonTextDe,
              select: () => setCategoryId(id),
            })),
          ...templates
            .filter(({ settings: { showAsOption } }) => showAsOption)
            .map(({ settings: { buttonTextEn, buttonTextDe }, story, storyCard }) => ({
              title: getLocale() === 'en-US' ? buttonTextEn : buttonTextDe,
              select: () => storyCardTemplateSelectHandler({ story, storyCard }),
            })),
        ].map((option, id) => ({ ...option, id })),
      [categories, storyCardTemplateSelectHandler, templates]
    );

    const addContentOptionClickHandler = useCallback(
      (id: number) => addContentOptions[id].select(),
      [addContentOptions]
    );

    const categoriesAsButton = useMemo(
      () =>
        categories
          .filter(({ settings: { showAsButton } }) => showAsButton)
          .map(({ id, settings: { buttonTextEn, buttonTextDe } }) => (
            <Button
              key={id}
              type={ButtonType.primary}
              label={getLocale() === 'en-US' ? buttonTextEn : buttonTextDe}
              onClick={() => setCategoryId(id)}
              fullWidth
            />
          )),
      [categories]
    );

    const templatesAsButton = useMemo(
      () =>
        templates
          .filter(({ settings: { showAsButton } }) => showAsButton)
          .map(({ id, settings: { buttonTextEn, buttonTextDe }, story, storyCard }) => (
            <Button
              key={id}
              type={ButtonType.primary}
              label={getLocale() === 'en-US' ? buttonTextEn : buttonTextDe}
              onClick={() => storyCardTemplateSelectHandler({ story, storyCard })}
              fullWidth
            />
          )),
      [storyCardTemplateSelectHandler, templates]
    );

    const category = useMemo(
      () => categories.find(({ id }) => id === categoryId),
      [categories, categoryId]
    );

    const categoryTemplates = useMemo(() => category?.templates ?? [], [category?.templates]);

    const onTemplateSelectCallback = useCallback((template: StoryCardCategoryTemplate) => {
      const { name, text, gallery } = template;

      setFilledFields({
        shortPostFilledFields: { sourceName: '', headline: name, subHeadline: '', text, gallery },
      });

      setIsCreateCardModalOpen(true);
    }, []);

    const onCreateCardModalClose = useCallback(() => {
      setIsCreateCardModalOpen(false);
      setIsTemplatePost(false);
      setFilledFields(undefined);
      setCategoryId(undefined);
      setPostInStoriesFiltered(postInStories);
    }, [postInStories]);

    useEffect(() => {
      if (isCreateCardModalOpen) {
        onMobilePanelClose?.();
      }
    }, [isCreateCardModalOpen, onMobilePanelClose]);
    return (
      <>
        {showForReader && (
          <Button
            type={ButtonType.primary}
            label={t('addContent.label')}
            onClick={() => setIsCreateCardModalOpen(true)}
            optionsOnly={!Boolean(postInStories.length)}
            options={addContentOptions}
            optionsWidth={'15.8rem'}
            onOptionClick={addContentOptionClickHandler}
            fullWidth
          />
        )}

        {categoriesAsButton}

        {templatesAsButton}

        {Boolean(category) && (
          <CategoryTemplateSelectionModal
            isOpen={Boolean(category && !isCreateCardModalOpen)}
            onClose={() => setCategoryId(undefined)}
            templates={categoryTemplates}
            onTemplateSelectCallback={onTemplateSelectCallback}
          />
        )}

        {isCreateCardModalOpen && (
          <CreateCardModal
            {...filledFields}
            isOpen={isCreateCardModalOpen}
            onClose={onCreateCardModalClose}
            postInStories={postInStoriesFiltered}
            card={{ category }}
            isTemplatePost={isTemplatePost}
          />
        )}
      </>
    );
  }
);
